import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Globe } from "lucide-react";
import { useI18n } from "@/i18n";

const LANGS = [
  { code: "es", label: "ES · Español" },
  { code: "en", label: "EN · English" },
  { code: "ar", label: "AR · العربية" },
];

/**
 * PdfLangMenu — a small dropdown that renders as a split-button attached to a
 * primary "Download PDF" trigger. Lets the operator choose the output language
 * per document without changing the whole UI locale.
 *
 * Props:
 *  - onPrint(lang, ...extra): callback invoked when the user picks a language.
 *  - label: main button label (defaults to "PDF").
 *  - icon: optional left-icon node.
 *  - className / testid: pass-through customisation.
 *  - compact: renders the compact icon-only variant (used in list rows).
 */
export function PdfLangMenu({ onPrint, label = "PDF", icon = null, className = "", testid = "pdf-lang", compact = false }) {
  const { lang: uiLang } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const pick = (code) => { setOpen(false); onPrint(code); };
  return (
    <div ref={ref} className={`inline-flex relative ${className}`} data-testid={testid}>
      <button
        type="button"
        onClick={() => pick(uiLang)}
        data-testid={`${testid}-main`}
        className={`inline-flex items-center gap-1.5 border border-slate-300 hover:border-red-600 hover:text-red-600 text-slate-700 rounded-l-lg font-semibold text-sm transition-colors ${compact ? "px-2 py-1.5" : "px-3 py-2"}`}
        title={`${label} (${uiLang.toUpperCase()})`}
      >
        {icon}
        {!compact && <span>{label}</span>}
      </button>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid={`${testid}-toggle`}
        className={`border border-l-0 border-slate-300 hover:border-red-600 hover:text-red-600 text-slate-500 rounded-r-lg transition-colors flex items-center ${compact ? "px-1.5 py-1.5" : "px-1.5 py-2"}`}
        title="Cambiar idioma / Change language"
      >
        <Globe className="w-3 h-3" />
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-lg shadow-lg min-w-[150px] overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-400 border-b border-slate-100">
            Report language
          </div>
          {LANGS.map(l => (
            <button
              key={l.code}
              type="button"
              onClick={() => pick(l.code)}
              data-testid={`${testid}-opt-${l.code}`}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-red-50 hover:text-red-700 transition-colors flex items-center justify-between ${l.code === uiLang ? "text-red-700 font-semibold bg-red-50/40" : "text-slate-700"}`}
            >
              <span>{l.label}</span>
              {l.code === uiLang && <span className="text-[10px] uppercase tracking-wider text-red-500">UI</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * PdfLangSelect — a plain <select> variant for forms/toolbars.
 * Used inside modal footers where inline dropdowns feel more natural.
 */
export function PdfLangSelect({ value, onChange, testid = "pdf-lang-select" }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="px-2 py-1.5 border border-slate-300 rounded-md text-xs bg-white outline-none focus:border-red-500"
      title="Idioma del reporte / Report language"
    >
      {LANGS.map(l => (
        <option key={l.code} value={l.code}>{l.label}</option>
      ))}
    </select>
  );
}

export const PDF_LANGS = LANGS;
